import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import {
	MatDateRangeInput,
	MatDateRangePicker,
	MatDatepickerToggle,
	MatEndDate,
	MatStartDate,
} from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { finalize } from 'rxjs/operators';
import { dateRangesOverlapValidator, DateRangesOverlapError } from '@/common/validators/date-ranges-overlap.validator';
import { ProductCostsApiService } from '../product-costs.api.service';
import { DateRangeGapInfo, ProductCost, ProductCostManageDialogData, ProductCostPrice } from '../product-costs.model';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProductService } from '../../product.service';

type PriceFormGroup = FormGroup<{
	rowId: FormControl<number>;
	start: FormControl<Date | null>;
	end: FormControl<Date | null>;
	price: FormControl<string | null>;
}>;

@Component({
	selector: 'app-product-cost-manage-dialog',
	standalone: true,
	imports: [
		MatDialogModule,
		MatButtonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatDateRangeInput,
		MatDateRangePicker,
		MatDatepickerToggle,
		MatStartDate,
		MatEndDate,
		MatProgressSpinnerModule,
	],
	templateUrl: './product-cost-manage-dialog.component.html',
	styleUrl: './product-cost-manage-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
})
export class ProductCostManageDialogComponent implements OnInit {
	readonly cost: ProductCost = inject<ProductCostManageDialogData>(MAT_DIALOG_DATA).cost;

	readonly isLoadingPrices = signal(false);
	readonly isSaving = signal(false);
	readonly pricesFormArray!: FormArray<PriceFormGroup>;
	readonly pricesFormArrayControls = signal<PriceFormGroup[]>([]);
	readonly dateRangeGaps = signal<DateRangeGapInfo[]>([]);

	private readonly fb = inject(FormBuilder);
	private readonly dialogRef = inject(MatDialogRef<ProductCostManageDialogComponent>);
	private readonly productCostsApiService = inject(ProductCostsApiService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dateAdapter = inject(DateAdapter);
	private readonly datePipe = inject(DatePipe);
	private readonly productService = inject(ProductService);
	private nextRowId = 0;

	constructor() {
		this.pricesFormArray = this.fb.array<PriceFormGroup>([], {
			validators: [dateRangesOverlapValidator()],
		});
	}

	ngOnInit(): void {
		this.dateAdapter.setLocale('pl-PL');

		this.isLoadingPrices.set(true);
		this.productCostsApiService
			.getProductCost(this.cost.id)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.isLoadingPrices.set(false)),
			)
			.subscribe((data) => {
				this.buildFormFromPrices(data.prices);
			});

		this.pricesFormArray.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.pricesFormArray.updateValueAndValidity({ onlySelf: true, emitEvent: false });
			this.dateRangeGaps.set([]);

			const err = this.pricesFormArray.errors?.['dateRangesOverlap'] as DateRangesOverlapError | null;
			for (const i of err?.indices ?? []) {
				this.touchRowIfOverlap(i);
			}
		});
	}

	addPrice(): void {
		this.pricesFormArray.push(this.createPriceFormGroup({ dateRange: { lower: '', upper: '' }, price: '' }));
		this.updatePricesFormArrayControls();
	}

	removePrice(index: number): void {
		this.pricesFormArray.removeAt(index);
		this.updatePricesFormArrayControls();
	}

	cancel(): void {
		this.dialogRef.close();
	}

	formatGapDate(date: Date): string {
		return this.datePipe.transform(date, 'dd.MM.yyyy') ?? '';
	}

	save(ignoreGaps = false): void {
		if (!ignoreGaps) {
			const gaps = this.findAllDateRangesGaps();
			if (gaps.length > 0) {
				this.dateRangeGaps.set(gaps);
				return;
			}
		}

		const prices = this.formValueToPrices();
		this.isSaving.set(true);
		this.productCostsApiService
			.patchProductCost(this.cost.id, { prices })
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.isSaving.set(false)),
			)
			.subscribe(() => {
				this.productService.data.costs = this.productService.data.costs.map((pc) =>
					pc.id === this.cost.id ? { ...pc, prices } : pc,
				);
				this.dialogRef.close(true);
			});
	}

	private findAllDateRangesGaps(): DateRangeGapInfo[] {
		const raw = this.pricesFormArray.getRawValue();
		const ranges: { startTs: number; endTs: number; endDate: Date; startDate: Date }[] = [];

		for (const v of raw) {
			const startTs = this.toDateOnlyTs(v.start);
			const endTs = this.toDateOnlyTs(v.end);
			if (startTs == null || endTs == null || startTs >= endTs || !v.start || !v.end) {
				continue;
			}
			ranges.push({
				startTs,
				endTs,
				startDate: v.start,
				endDate: v.end,
			});
		}

		if (ranges.length < 2) {
			return [];
		}

		ranges.sort((a, b) => a.startTs - b.startTs);
		const oneDayMs = 24 * 60 * 60 * 1000;
		const result: DateRangeGapInfo[] = [];

		for (let i = 0; i < ranges.length - 1; i++) {
			const curr = ranges[i];
			const next = ranges[i + 1];
			if (next.startTs > curr.endTs) {
				const gapDays = Math.round((next.startTs - curr.endTs) / oneDayMs);
				result.push({
					endOfFirst: curr.endDate,
					startOfSecond: next.startDate,
					gapDays,
				});
			}
		}

		return result;
	}

	private toDateOnlyTs(value: unknown): number | null {
		if (value == null) {
			return null;
		}
		const date = value instanceof Date ? value : new Date(value as string);
		if (Number.isNaN(date.getTime())) {
			return null;
		}
		const d = new Date(date);
		d.setHours(0, 0, 0, 0);
		return d.getTime();
	}

	private touchRowIfOverlap(index: number): void {
		const group = this.pricesFormArray.at(index) as PriceFormGroup;
		if (!group) {
			return;
		}

		const err = this.pricesFormArray.errors?.['dateRangesOverlap'] as DateRangesOverlapError | null;
		const overlaps = (err?.indices ?? []).includes(index);

		if (overlaps) {
			group.controls.start.markAsTouched({ onlySelf: true });
			group.controls.end.markAsTouched({ onlySelf: true });
		}
	}

	private formatDateToLocalIso(date: Date): string {
		return this.datePipe.transform(date, 'yyyy-MM-dd') ?? '';
	}

	private formValueToPrices(): ProductCostPrice[] {
		return this.pricesFormArray.getRawValue().map((v) => ({
			dateRange: {
				lower: v.start ? this.formatDateToLocalIso(v.start) : '',
				upper: v.end ? this.formatDateToLocalIso(v.end) : '',
			},
			price: v.price ?? '',
		}));
	}

	private createPriceFormGroup(p: ProductCostPrice): PriceFormGroup {
		return this.fb.group({
			rowId: new FormControl<number>(this.nextRowId++, { nonNullable: true }),
			start: new FormControl<Date | null>(
				p.dateRange.lower ? new Date(p.dateRange.lower) : null,
				Validators.required,
			),
			end: new FormControl<Date | null>(
				p.dateRange.upper ? new Date(p.dateRange.upper) : null,
				Validators.required,
			),
			price: new FormControl<string | null>(p.price, Validators.required),
		}) as PriceFormGroup;
	}

	private buildFormFromPrices(pricesList: ProductCostPrice[]): void {
		this.pricesFormArray.clear();
		pricesList.forEach((p) => this.pricesFormArray.push(this.createPriceFormGroup(p)));
		this.pricesFormArray.updateValueAndValidity({ emitEvent: false });
		this.dateRangeGaps.set([]);
		this.updatePricesFormArrayControls();
	}

	private updatePricesFormArrayControls(): void {
		this.pricesFormArrayControls.set(this.pricesFormArray.controls as PriceFormGroup[]);
	}
}
